/**
 * `determinize` — sample one fully-known world consistent with a player's view
 * (SPEC.md §4.4, §6.1; CONTEXT.md "Determinization").
 *
 * The AI's search needs a concrete world to play out. `determinize(state, player, rng)`
 * returns a copy of the state in which every card the perspective player cannot identify
 * has been given a plausible identity, drawn uniformly from the cards that could be there.
 *
 * **Only `defId`s move.** Instance ids, zone arrays and their order, controllers, exertion,
 * damage, lore, the resolution stack — every scrap of public structure is untouched by
 * construction (SPEC.md §4.4: "instance ids never move"), so a determinized state is
 * interchangeable with the real one everywhere except in what the cards turn out to be.
 *
 * Three constraints keep the sample honest:
 *
 * 1. **Cards the viewer knows keep their identity.** Their own hand, their own inkwell, a
 *    card they saw returned to an opponent's hand — all pinned.
 * 2. **Identities stay with their owner.** `CardInstance.owner` is public and never moves,
 *    so a card is only ever swapped with another card from the same deck list. That is what
 *    preserves "I know what is left in my own deck" without any extra bookkeeping.
 * 3. **What the viewer knows is in a deck stays in that deck** — the revealed-then-shuffled
 *    leak. The viewer knows the card is in there but not *where*, so its identity is dealt
 *    to a random position among that deck's cards.
 *
 * Only the **perspective player's** knowledge is guaranteed faithful in the result: their
 * `knownTo` bits follow the identities they are knowledge of. Other players' bits stay with
 * the slot they were on, which is as meaningful as anything else about a sampled world —
 * nothing should read another seat's view out of a determinization.
 *
 * This is the **only** engine entry point that takes an external RNG (SPEC.md §4.2): every
 * other random decision draws from `GameState.rng`, which is cloned with the state and
 * replays exactly. `determinize` neither reads nor advances `state.rng`.
 */
import { clone } from './clone.ts';
import { recomputePending } from './engine.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import type { RngState } from './rng.ts';
import { shuffleInPlace } from './rng.ts';
import type { GameState } from './state.ts';
import { instanceAt, playerBit } from './state.ts';
import { isVisibleTo, knowsCard } from './view.ts';

/** The hidden cards of one deck list, split by the constraint that applies to them. */
interface OwnerSlots {
  /** Ids of this owner's cards in their deck — a deck's positions are never known. */
  readonly deckSlots: InstanceId[];
  /** Ids of this owner's other cards the viewer cannot identify (hands, inkwells). */
  readonly otherSlots: InstanceId[];
  /** Identities the viewer knows are in that deck; they may be reordered, not relocated. */
  readonly pinnedToDeck: number[];
  /** Every other hidden identity of this owner; free to land in any of their hidden slots. */
  readonly free: number[];
}

/**
 * A random world consistent with `player`'s view: `playerView(determinize(s, p, r), p)`
 * always deep-equals `playerView(s, p)`.
 *
 * The input state is never mutated; the result is an independent deep copy carrying the
 * same `rng` counter, so applying actions to it continues the game's own random sequence.
 * Determinizing an already-determinized state draws a fresh sample from the same
 * information set, which is what lets a searcher re-determinize per iteration.
 */
export function determinize(state: GameState, player: PlayerIndex, rng: RngState): GameState {
  const next = clone(state);
  const viewerBit = playerBit(player);
  const byOwner = new Map<PlayerIndex, OwnerSlots>();

  // One pass over the dense instance array, so the sample is a deterministic function of
  // (state, player, rng) and never depends on iteration order.
  for (const instance of next.instances) {
    if (isVisibleTo(next, player, instance.id)) continue;
    let slots = byOwner.get(instance.owner);
    if (slots === undefined) {
      slots = { deckSlots: [], otherSlots: [], pinnedToDeck: [], free: [] };
      byOwner.set(instance.owner, slots);
    }
    if (instance.zone === 'deck') {
      slots.deckSlots.push(instance.id);
      if (knowsCard(next, player, instance.id)) slots.pinnedToDeck.push(instance.defId);
      else slots.free.push(instance.defId);
    } else {
      slots.otherSlots.push(instance.id);
      slots.free.push(instance.defId);
    }
  }

  for (const slots of byOwner.values()) {
    // Shuffling the deck's slots — not its contents — is what randomises the position of
    // the identities the viewer knows are in there.
    const deckSlots = [...slots.deckSlots];
    shuffleInPlace(deckSlots, rng);
    const free = [...slots.free];
    shuffleInPlace(free, rng);

    const pinnedCount = slots.pinnedToDeck.length;
    for (let i = 0; i < pinnedCount; i += 1) {
      const instance = instanceAt(next, deckSlots[i] as InstanceId);
      instance.defId = slots.pinnedToDeck[i] as number;
      // The viewer's knowledge follows the identity it is knowledge *of*.
      instance.knownTo |= viewerBit;
    }
    const freeSlots = [...deckSlots.slice(pinnedCount), ...slots.otherSlots];
    for (let i = 0; i < freeSlots.length; i += 1) {
      const instance = instanceAt(next, freeSlots[i] as InstanceId);
      instance.defId = free[i] as number;
      instance.knownTo &= ~viewerBit;
    }
  }

  retellEvents(next);
  // The pending options were enumerated from the real cards; in this world the hand that
  // produced them may hold different ones (`play:d42` must name a card that is there).
  if (next.result === null && next.pending.kind !== 'game-over') recomputePending(next);
  return next;
}

/**
 * Re-derive the `defId` an event names from the card it names, so a determinized state's
 * event stream describes the determinized cards. Redaction is unaffected — an event about
 * a card the viewer cannot identify reads `null` in either world.
 */
function retellEvents(state: GameState): void {
  state.events = state.events.map((event) => {
    if (!('defId' in event)) return event;
    const named = 'instance' in event ? event.instance : event.source;
    return { ...event, defId: instanceAt(state, named).defId };
  });
}
